import { colors, sizes, spacings } from "@/assets/themes";
import {
  Accordion,
  Avatar,
  Block,
  Body16,
  Container,
  Divider,
  H1,
  H2,
  H4,
  Icon,
  List,
} from "@/components";
import { CardBase } from "@/components/Styles/Base";
import { LIST } from "@/components/Styles/variants";
import { useBreakpoints } from "@/modules/hooks";
import {
  useGetBookingData,
  useSubmitBooking,
} from "@/modules/routes/jobber-booking-routes";
import { useTokenizeQuery } from "@/modules/routes/payment-routes";
import {
  formatDate,
  getTranslatedDay,
  paymentCreditCardValidation,
  setPaymentInitialValues,
} from "@/utils";
import polyglot from "@/utils/polyglot";
import { Form, Formik } from "formik";
import { useMemo, useRef } from "react";
import * as yup from "yup";
import PaymentMethodsUpdated from "../common/payment-methods";
import MembershipAdvantages from "../dashboard/account/membership/MembershipAdvantages";
import JobberOfferItem from "../dashboard/jobs/JobberOfferItem";
import Aside from "./Aside";
import Faq from "./Faq";
import JobberBookingPassSubmitButton from "./JobberBookingPassSubmitButton";
import Methods from "../common/payment-methods/Methods";

const JobberBookingRecurring = ({ jobber }) => {
  const { data } = useGetBookingData();

  const offerWithoutPrice = useMemo(
    () => ({
      ...data.offer,
      price: undefined,
      price_per_hour: undefined,
    }),
    [data.offer]
  );

  const submit = useSubmitBooking();
  const formikRef = useRef();
  const paymentMethodRef = useRef();
  const submitBooking = useTokenizeQuery(
    submit.mutateAsync,
    data?.payment_methods
  );

  const isLoading = submitBooking.isLoading || submitBooking.isSuccess;

  const breakpoints = useBreakpoints();

  const handleSubmit = (values) => {
    submitBooking.mutate(values, {
      onError: (err) => {
        const mustResetCard = err.response.data?.data?.must_reset_card;
        if (mustResetCard) {
          paymentMethodRef.current?.reset();
        }
      },
    });
  };

  const getRecurringJobDate = () =>
    polyglot.t("job.from_datetime", {
      date: formatDate(data?.job.start_date, "dddd DD MMMM"),
      start_hour: polyglot.toTime(data.job.start_hour),
      end_hour: polyglot.toTime((data.job.start_hour + data.job.nb_hours) % 24),
      nb_hours: polyglot.toDuration(data.job.nb_hours, true, true),
    });

  const getRecurringJobFrequency = () =>
    polyglot.t(`frequency.with_context.${data?.job.frequency}`, {
      day: getTranslatedDay(data?.job.wday).toLowerCase(),
      day_number: formatDate(data?.job.start_date, "DD"),
      position:
        data?.job.wposition &&
        [
          polyglot.t("frequency.first"),
          polyglot.t("frequency.second"),
          polyglot.t("frequency.third"),
          polyglot.t("frequency.last"),
        ][data?.job.wposition - 1]?.toLowerCase(),
    });

  const recurringJobData = useMemo(
    () => [
      {
        label: data?.job.title,
        icon: "work",
      },
      {
        label: getRecurringJobDate(),
        icon: "calendar",
      },
      {
        label: data?.job.address,
        icon: "map-marker",
      },
      {
        label: getRecurringJobFrequency(),
        icon: "refresh",
      },
      {
        label: polyglot.t("booking.hourly_rate", {
          price_per_hour: polyglot.toSmartCurrency(data?.offer.price_per_hour, {
            pricePerHour: true,
            noDecimals: true,
          }),
        }),
        icon: "wallet",
      },
    ],
    [data]
  );

  return (
    <div>
      <Container.Medium>
        <Block
          display="grid"
          gridCols={{ xs: "1fr", md: "1fr 350px" }}
          gap={spacings.l}
        >
          <Block>
            <Formik
              innerRef={formikRef}
              onSubmit={handleSubmit}
              validationSchema={yup.lazy(() => {
                if (!data.membership.is_active && data.membership.price > 0) {
                  return paymentCreditCardValidation;
                }
                return yup.object().shape({});
              })}
              validateOnMount
              initialValues={{
                ...setPaymentInitialValues({
                  ...data,
                }),
              }}
            >
              <Form>
                <Block spaceY={spacings.m}>
                  <CardBase flat>
                    <Accordion.Group
                      active={data.membership.is_active ? 0 : undefined}
                    >
                      <Accordion
                        active
                        withGutters
                        divider={false}
                        title={polyglot.t("job.recurring_summary_title", {
                          first_name: data.offer.jobber.first_name,
                        })}
                        subtitle={`${
                          data?.job.title
                        }, ${getRecurringJobFrequency().toLowerCase()}`}
                        LeftComponent={() => (
                          <Avatar
                            size={sizes.size54}
                            src={jobber.avatar}
                            name={jobber.first_name}
                          />
                        )}
                      >
                        {recurringJobData.map((item) => (
                          <List.Item
                            divider={false}
                            key={item.icon}
                            LeftComponent={() => (
                              <Icon.Large name={item.icon} />
                            )}
                          >
                            <Body16 strong>{item.label}</Body16>
                          </List.Item>
                        ))}
                      </Accordion>
                    </Accordion.Group>
                    {data.membership.is_active && (
                      <JobberBookingPassSubmitButton
                        isLoading={isLoading}
                        data={data}
                        onSubmit={handleSubmit}
                      />
                    )}
                  </CardBase>
                </Block>
                <Block>
                  {!data.membership.is_active && (
                    <Block>
                      <List.Header
                        subtitle={polyglot.t(
                          "membership.subscription_no_obligation"
                        )}
                        RightComponent={() => (
                          <H2>
                            {polyglot.toSmartCurrency(data.membership.price)}
                          </H2>
                        )}
                      >
                        <H2>{polyglot.t("routes.pass")}</H2>
                        <Body16
                          css={`
                            font-weight: var(--font-weight-regular);
                          `}
                        >
                          {polyglot.t("membership.subscription_no_obligation")}
                        </Body16>
                      </List.Header>

                      <MembershipAdvantages withGutters />

                      <List.Header
                        RightComponent={
                          breakpoints.get({
                            xs: false,
                            sm: true,
                          })
                            ? () => <Methods methods={data.payment_methods} />
                            : undefined
                        }
                      >
                        <div>{polyglot.t("payment_methods.title")}</div>
                      </List.Header>
                      <CardBase flat>
                        <PaymentMethodsUpdated
                          ref={paymentMethodRef}
                          withGutters
                          divider={false}
                          mangopay_cards={data.mangopay_cards}
                          methods={data.payment_methods}
                        />
                        <JobberBookingPassSubmitButton
                          isLoading={isLoading}
                          data={data}
                          onSubmit={handleSubmit}
                        />
                      </CardBase>
                    </Block>
                  )}

                  {data?.zendesk_articles?.length > 0 && (
                    <Block marginTop={spacings.m}>
                      <CardBase flat>
                        <Block
                          paddingBottom={spacings.sm}
                          paddingTop={{ xs: spacings.xs, md: spacings.sm }}
                        >
                          <Block paddingX={{ xs: spacings.m, md: spacings.ml }}>
                            <Faq data={data?.zendesk_articles} />
                          </Block>
                        </Block>
                      </CardBase>
                    </Block>
                  )}
                </Block>
              </Form>
            </Formik>
          </Block>

          <Block>
            {breakpoints.get({
              md: (
                <Block>
                  <Aside
                    job={data.job}
                    subtitle={getRecurringJobFrequency()}
                    jobber={jobber}
                    offer={offerWithoutPrice}
                    show_phone_assistance={data.show_phone_assistance}
                    hideSummary
                  />
                </Block>
              ),
            })}
          </Block>
        </Block>
      </Container.Medium>
    </div>
  );
};

export default JobberBookingRecurring;
